import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import { Shop, ShopWithAnnex } from '../../types';
import ShopCard from '../ShopCard/ShopCard.index';

export interface MapSearchProps {
  shops: Shop[];
  currentLocation: { lat: number; lng: number } | undefined;
  selectedShopId: string | null | undefined;
  isAnnexSelected: boolean;
  onShopClick: (shopId: string, isAnnex: boolean) => void;
  onBookmark: (shopId: string) => void;
  onReserve: (shopId: string, shopName: string, preOpenDay: string) => void;
}

declare global {
  interface Window {
    L: any;
  }
}

const MapSearch: FC<MapSearchProps> = ({
  shops,
  currentLocation,
  selectedShopId = null,
  isAnnexSelected = false,
  onShopClick,
  onBookmark,
  onReserve,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [selectedShop, setSelectedShop] = useState<ShopWithAnnex | null>(null);
  const [shopCardPosition, setShopCardPosition] = useState<{ x: number; y: number } | null>(null);
  const markersRef = useRef<any[]>([]);
  const selectedShopRef = useRef<ShopWithAnnex | null>(null);
  const [shopListWithAnnex, setShopListWithAnnex] = useState<ShopWithAnnex[]>([]);

  useEffect(() => {
    if (shops.length > 0) {
      const shopList: ShopWithAnnex[] = [];
      shops.forEach((shop) => {
        if (shop.latitude && shop.longitude) {
          const shopItem: ShopWithAnnex = {
            ...shop,
            isAnnex: false,
            latitude: shop.latitude,
            longitude: shop.longitude,
            annexAddress: '',
            annexOpenDate: '',
          };
          if (shop.annexFlag) {
            if (shop.annexLatitude && shop.annexLongitude) {
              if (shop.latitude === shop.annexLatitude && shop.longitude === shop.annexLongitude) {
                shopItem.annexAddress = shop.annexAddress || '';
                shopItem.annexOpenDate = shop.annexOpenDate || '';
              } else {
                shopList.push({
                  ...shop,
                  isAnnex: true,
                  latitude: shop.annexLatitude,
                  longitude: shop.annexLongitude,
                  address: shop.annexAddress || '',
                  businessHours: shop.annexOpenDate || '',
                });
              }
            }
          }
          shopList.push(shopItem);
        }
      });
      setShopListWithAnnex(shopList);
    }
  }, [shops]);

  useEffect(() => {
    selectedShopRef.current = selectedShop;
  }, [selectedShop]);

  const clearMarkers = useCallback((mapInstance: any) => {
    markersRef.current.forEach((marker) => {
      mapInstance.removeLayer(marker);
    });
    markersRef.current = [];
  }, []);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (!window.L) {
      return;
    }

    if ((mapRef.current as any)._leaflet_id) {
      try {
        const existingMap = (mapRef.current as any)._leaflet_map;
        if (existingMap) {
          existingMap.remove();
        }
      } catch (error) {
        console.error(error);
      }
    }

    let mapInstance: any = null;

    try {
      mapInstance = window.L.map(mapRef.current, {
        center: [35.6762, 139.6503],
        zoom: 13,
        zoomControl: false,
        preferCanvas: false,
        worldCopyJump: true,
      });

      (mapRef.current as any)._leaflet_map = mapInstance;

      const tileLayer = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      });

      tileLayer.addTo(mapInstance);
      setTimeout(() => {
        if (mapInstance && mapRef.current) {
          mapInstance.invalidateSize();
        }
      }, 100);

      setMap(mapInstance);
    } catch (error) {
      console.error(error);
    }

    return () => {
      if (mapInstance) {
        try {
          clearMarkers(mapInstance);
          mapInstance.remove();
          if (mapRef.current) {
            delete (mapRef.current as any)._leaflet_map;
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (map && currentLocation) {
      map.setView([currentLocation.lat, currentLocation.lng], map.getZoom());
    }
  }, [map, currentLocation]);

  const [isCardShow, setIsCardShow] = useState(false);
  const [zoomInActive, setZoomInActive] = useState(true);
  const [zoomOutActive, setZoomOutActive] = useState(true);

  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      if (map.getZoom() < map.getMinZoom() || map.getZoom() === map.getMinZoom()) {
        setZoomOutActive(false);
      } else {
        setZoomOutActive(true);
      }
      if (map.getZoom() > map.getMaxZoom() || map.getZoom() === map.getMaxZoom()) {
        setZoomInActive(false);
      } else {
        setZoomInActive(true);
      }
    };

    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !selectedShop) return;

    const updateCardPosition = () => {
      if (selectedShop.latitude && selectedShop.longitude) {
        const point = map.latLngToContainerPoint([selectedShop.latitude, selectedShop.longitude]);
        setShopCardPosition({ x: point.x, y: point.y });
      }
    };

    const handleMapClick = () => {
      setSelectedShop(null);
      setShopCardPosition(null);
      setIsCardShow(false);
      selectedShopRef.current = null;
    };

    map.on('move', updateCardPosition);
    map.on('mousedown', handleMapClick);
    map.on('dragstart', handleMapClick);

    return () => {
      map.off('move', updateCardPosition);
      map.off('mousedown', handleMapClick);
      map.off('dragstart', handleMapClick);
    };
  }, [map, selectedShop]);

  useEffect(() => {
    if (!map || !window.L) {
      return;
    }

    clearMarkers(map);

    if (currentLocation) {
      const currentLocationIcon = window.L.divIcon({
        className: 'leaflet-marker-icon !border-none !bg-transparent',
        html: '<img src="/assets/img/icon-map-pin.svg" alt="現在地" class="p-icon p-icon--size45">',
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      });

      const currentMarker = window.L.marker([currentLocation.lat, currentLocation.lng], {
        icon: currentLocationIcon,
      }).addTo(map);

      markersRef.current.push(currentMarker);
    }

    const validShops = shopListWithAnnex.filter((shop) => {
      const isLocationValid = shop.longitude && shop.latitude;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const openDay = shop.annexOpenDay ? new Date(shop.annexOpenDay) : null;
      if (openDay !== null) {
        openDay.setHours(0, 0, 0, 0);
      }
      const closedDay = shop.annexClosedDay ? new Date(shop.annexClosedDay) : null;
      if (closedDay !== null) {
        closedDay.setHours(0, 0, 0, 0);
      }
      const isAnnexValid =
        shop.isAnnex && openDay !== null && openDay <= now && (closedDay === null || closedDay >= now);
      return isLocationValid && (!shop.isAnnex || isAnnexValid);
    });

    validShops.forEach((shop) => {
      const { longitude, latitude } = shop;

      if (!longitude || !latitude) return;

      const isSelected = selectedShopId === shop.id && isAnnexSelected === shop.isAnnex;
      const shopIcon = window.L.divIcon({
        className: `leaflet-marker-icon w-12 hover:!z-mapUI ${isSelected ? 'selected' : ''}`,
        html: `
          <div class="absolute bottom-0 grid w-min justify-items-center transition-all hover:z-mapUI hover:scale-110 hover:cursor-pointer">
            <img
              src="/assets/img/map-pin-image.png"
              class="h-12 !w-12 border border-[#ccc] bg-white object-cover drop-shadow-mappin rounded-3xl"
              alt="${shop.name}"
              style="border-radius: 50%;"
            />
            <div class="grid justify-center">
              <div class="h-3 w-1 bg-custom-map-pin"></div>
              <div class="w-1 border-x-2 border-b-0 border-t-4 border-x-transparent border-b-transparent border-t-custom-map-pin"></div>
            </div>
          </div>
        `,
        iconSize: [48, 64],
        iconAnchor: [24, 64],
      });

      const marker = window.L.marker([latitude, longitude], { icon: shopIcon }).addTo(map);

      marker.on('mousedown', (e: any) => {
        window.L.DomEvent.stopPropagation(e);
      });
      marker.on('dragstart', (e: any) => {
        window.L.DomEvent.stopPropagation(e);
      });
      marker.on('click', async (e: any) => {
        if (selectedShopRef.current?.id === shop.id && selectedShopRef.current.isAnnex === shop.isAnnex && isCardShow) {
          setIsCardShow(false);
          selectedShopRef.current = null;
          setShopCardPosition(null);
          setSelectedShop(null);
        } else {
          setIsCardShow(true);
          selectedShopRef.current = shop;
          const point = map.latLngToContainerPoint([latitude, longitude]);
          setShopCardPosition({ x: point.x, y: point.y });
          setSelectedShop(shop);
        }
        window.L.DomEvent.stopPropagation(e);

        onShopClick(shop.id, shop.isAnnex);

        const invalidateSizePromise = () => {
          return new Promise<void>((resolve) => {
            map.invalidateSize();

            requestAnimationFrame(() => {
              resolve();
            });
          });
        };

        await invalidateSizePromise();

        const targetPoint = map.latLngToContainerPoint([latitude, longitude]);
        const offsetPoint = window.L.point(targetPoint.x, targetPoint.y);
        const offsetLatLng = map.containerPointToLatLng(offsetPoint);

        const setViewPromise = () => {
          return new Promise<void>((resolve) => {
            map.setView(offsetLatLng, map.getZoom(), {
              animate: true,
              duration: 0.5,
            });
            const handleMoveEnd = () => {
              map.off('moveend', handleMoveEnd);
              resolve();
            };
            map.on('moveend', handleMoveEnd);
          });
        };

        await setViewPromise();
      });

      markersRef.current.push(marker);
    });

    if (selectedShop) {
      const newSelectedShop =
        shopListWithAnnex.find((shop) => shop.id === selectedShop.id && shop.isAnnex === selectedShop.isAnnex) || null;
      setSelectedShop(newSelectedShop);
    }
  }, [map, shops, selectedShopId, currentLocation, onShopClick, clearMarkers, shopListWithAnnex]);

  const handleZoomIn = () => {
    if (map) {
      map.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (map) {
      map.zoomOut();
    }
  };

  return (
    <figure className="p-map-search relative grow overflow-hidden">
      <div
        ref={mapRef}
        id="map-container"
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
          minHeight: '600px',
        }}
      />

      {isCardShow && selectedShop && shopCardPosition && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
        <div
          className="p-map-store"
          style={{
            opacity: 1,
            position: 'absolute',
            left: `${shopCardPosition.x}px`,
            bottom: `${map.getSize().y - shopCardPosition.y + 50}px`,
            transform: 'translate(-50%)',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ShopCard shop={selectedShop} onBookmark={onBookmark} onReserve={onReserve} isMapVersion />
        </div>
      )}

      <div className="p-map-search__bottom-tool">
        <div className="p-zoom-control">
          <button
            type="button"
            className={`p-zoom-control__button p-zoom-control__button--in ${
              zoomInActive ? '' : 'p-zoom-control__button--disabled'
            }`}
            title="Zoom in"
            aria-label="Zoom in"
            onClick={handleZoomIn}
          />
          <button
            type="button"
            className={`p-zoom-control__button p-zoom-control__button--out ${
              zoomOutActive ? '' : 'p-zoom-control__button--disabled'
            }`}
            title="Zoom out"
            aria-label="Zoom out"
            onClick={handleZoomOut}
          />
        </div>
      </div>
    </figure>
  );
};

export default MapSearch;
